// react
import { SyntheticEvent, useState } from 'react';

// types
import { Quarter, StatsGeneral } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useNavigate, useParams } from 'react-router-dom';

// context
import { ContextDashboard } from './Dashboard.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Container, Grid, Typography } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// layouts
import { Authenticated } from '../../layouts/Authenticated/Authenticated';

// ui
import { Breadcrumbs } from '../../ui/Breadcrumbs/Breadcrumbs';
import { Tabs } from '../../ui/Tabs/Tabs';
import { Time } from '../../ui/Time/Time';
import { Title } from '../../ui/Title/Title';

// fields
import { Select } from '../../fields/Select/Select';

// pages
import { Adoption } from './Adoption/Adoption';
import { Breakdown } from './Breakdown/Breakdown';
import { Series } from './Series/Series';
import { Summary } from './Summary/Summary';

// utils
import { getFiscalYearDates } from '../../../utils/dates';

export const PageDashboard = () => {
	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { tab: tabParam } = useParams<{ tab: string }>();

	// tabs configuration
	const tabs = [
		{ label: t('projects'), value: 'projects' },
		{ label: t('financing'), value: 'loans' },
		{ label: t('accounts'), value: 'accounts' },
		{ label: t('entities'), value: 'entities' },
		{ label: t('users'), value: 'users' },
		{ label: t('contracts'), value: 'contracts' },
		{ label: t('transactions'), value: 'transactions' },
	];

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [filters, setFilters] = useState({ fiscalYear: 'all', period: 'all' });
	const [dataGeneral, setDataGeneral] = useState<StatsGeneral | null>(null);
	const [dataResource, setDataResource] = useState<StatsGeneral | null>(null);
	const [dateEarliest, setDateEarliest] = useState<Date | null>(null);

	// fetch earliest project to determine fiscal year range
	useFetch({
		url: `${process.env.REACT_APP_API_URL}/projects?fields=created,id&order=ASC&orderBy=created&limit=1`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data && res.data.length > 0) {
				const dateEarliest = new Date(res.data[0].created);
				setDateEarliest(dateEarliest);
			}
		},
	});

	// use URL param as source of truth for tab
	const tab = tabParam || tabs[0].value;

	let dateStart: Date | null = null;
	let dateEnd: Date | null = null;

	if (filters.fiscalYear !== 'all' || filters.period !== 'all') {
		// calculate date range from filters
		const dates = getFiscalYearDates({
			fiscalYear: Number(filters.fiscalYear),
			quarter: filters.period as Quarter,
		});

		dateEnd = dates.dateEnd;
		dateStart = dates.dateStart;
	}

	// build query params with date strings
	const queryParams = new URLSearchParams();
	if (dateStart) {
		queryParams.append('dateStart', dateStart.toISOString());
	}
	if (dateEnd) {
		queryParams.append('dateEnd', dateEnd.toISOString());
	}
	const queryString = queryParams.toString();

	// fetch general stats
	const { isLoading: isLoadingGeneral, refetch: refetchGeneral } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/stats/general${queryString ? `?${queryString}` : ''}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDataGeneral(res.data);
			}
		},
	});

	// fetch resource-specific stats
	const { isLoading: isLoadingResource, refetch: refetchResource } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/stats/${tab}${queryString ? `?${queryString}` : ''}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDataResource(res.data);
			}
		},
	});

	// fiscal year options (from earliest project to current year + 1)
	let yearEarliest;

	if (dateEarliest) {
		const dateEarliestYear = dateEarliest.getFullYear();
		// fiscal year starts in July, so if project created before July, use previous year
		yearEarliest = dateEarliestYear + 1;
		if (dateEarliest.getMonth() < 6) {
			yearEarliest = dateEarliestYear;
		}
	}

	const yearCurrent = new Date().getFullYear();
	const yearStart = yearEarliest || yearCurrent;
	const yearEnd = yearCurrent + 1;
	const yearCount = yearEnd - yearStart + 1;

	const optionsFiscalYear = Array.from({ length: yearCount }, (_, i) => {
		const year = yearEnd - i;
		return {
			label: year,
			value: String(year),
		};
	});

	// period options
	const optionsPeriod = [
		{ label: 'Q1', value: 'Q1' },
		{ label: 'Q2', value: 'Q2' },
		{ label: 'Q3', value: 'Q3' },
		{ label: 'Q4', value: 'Q4' },
	];

	// handlers
	const handleChangeTab = (event: SyntheticEvent, newValue: string) => {
		navigate(`/dashboard/${newValue}`);
	};

	return (
		<Authenticated>
			<ContextDashboard.Provider
				value={{
					filters,
					isLoadingGeneral,
					isLoadingResource,
					refetchGeneral,
					refetchResource,
					setFilters,
					statsGeneral: dataGeneral || null,
					statsResource: dataResource || null,
				}}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Breadcrumbs
							crumbs={[
								{
									children: t('dashboard'),
									to: '/dashboard',
								},
							]}
							page={t('dashboard')}
						/>
					</Grid>

					<Grid item xs={12}>
						<Title
							color="purple"
							IconCircleProps={{ type: 'project' }}
							overline={t('projects')}
							title={t('dashboard')}
						/>
					</Grid>
					<Grid item xs={12}>
						<Container maxWidth={false}>
							<Grid alignItems="flex-end" container justifyContent="space-between" spacing={2}>
								<Grid item xs={12} md="auto">
									<Grid alignItems="center" container spacing={2}>
										<Grid item>
											<Typography variant="h2">{t('summary')}</Typography>
										</Grid>
										{dateStart && dateEnd && (
											<Grid item>
												<Typography variant="body2">
													<Time date={dateStart} />
													{` - `}
													<Time date={dateEnd} />
												</Typography>
											</Grid>
										)}
									</Grid>
								</Grid>
								<Grid item xs={12} md={6} lg={4} sx={{ marginTop: { xs: -1, md: -4 } }}>
									<Grid container spacing={2}>
										<Grid item xs={6}>
											<Select
												hasAllOption={true}
												hasNoneOption={false}
												label={t('fiscalYear')}
												onChange={(e) => {
													const value = e.target.value as string;

													setFilters((filtersPrev) => {
														const filtersNew = { ...filtersPrev, fiscalYear: value };

														// set period to all if fiscal year is all
														if (value === 'all') {
															filtersNew.period = 'all';
														}
														return filtersNew;
													});
												}}
												options={optionsFiscalYear}
												value={filters.fiscalYear}
											/>
										</Grid>
										<Grid item xs={6}>
											<Select
												disabled={filters.fiscalYear === 'all'}
												hasAllOption={true}
												hasNoneOption={false}
												label={t('period')}
												onChange={(e) =>
													setFilters((filtersPrev) => {
														const filtersNew = { ...filtersPrev, period: e.target.value as string };
														return filtersNew;
													})
												}
												options={optionsPeriod}
												value={filters.period}
											/>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Container>
					</Grid>

					<Grid item xs={12}>
						<Summary />
					</Grid>

					<Grid item xs={12}>
						<Container maxWidth={false}>
							<Typography variant="h2">{t('details')}</Typography>
						</Container>
					</Grid>

					<Grid item xs={12}>
						<Tabs onChange={handleChangeTab} tabs={tabs} value={tab} />
					</Grid>

					<Grid item xs={12}>
						<Container maxWidth={false}>
							<TabContext value={tab}>
								<TabPanel value="projects">
									<Grid container spacing={2}>
										<Grid item lg={4} xs={12}>
											<Breakdown type="projects" />
										</Grid>
										<Grid item lg={8} xs={12}>
											<Series type="projects" />
										</Grid>
									</Grid>
								</TabPanel>

								<TabPanel value="loans">
									<Grid container spacing={2}>
										<Grid item lg={4} xs={12}>
											<Breakdown type="loans" />
										</Grid>
										<Grid item lg={8} xs={12}>
											<Series type="loans" />
										</Grid>
									</Grid>
								</TabPanel>

								<TabPanel value="accounts">
									<Grid container spacing={2}>
										<Grid item lg={4} xs={12}>
											<Breakdown type="accounts" />
										</Grid>
										<Grid item lg={8} xs={12}>
											<Series type="accounts" />
										</Grid>
									</Grid>
								</TabPanel>

								<TabPanel value="entities">
									<Grid container spacing={2}>
										<Grid item lg={4} xs={12}>
											<Breakdown type="entities" />
										</Grid>
										<Grid item lg={8} xs={12}>
											<Series type="entities" />
										</Grid>
									</Grid>
								</TabPanel>

								<TabPanel value="users">
									<Grid container spacing={2}>
										<Grid item lg={4} xs={12}>
											<Breakdown type="users" />
										</Grid>
										<Grid item lg={8} xs={12}>
											<Series type="users" />
										</Grid>
									</Grid>
								</TabPanel>

								<TabPanel value="contracts">
									<Grid container spacing={2}>
										<Grid item lg={4} xs={12}>
											<Breakdown type="contracts" />
										</Grid>
										<Grid item lg={8} xs={12}>
											<Series type="contracts" />
										</Grid>
									</Grid>
								</TabPanel>

								<TabPanel value="transactions">
									<Grid container spacing={2}>
										<Grid item lg={4} xs={12}>
											<Breakdown type="transactions" />
										</Grid>
										<Grid item lg={8} xs={12}>
											<Series type="transactions" />
										</Grid>
									</Grid>
								</TabPanel>
							</TabContext>
						</Container>
					</Grid>

					<Grid item xs={12}>
						<Adoption />
					</Grid>
				</Grid>
			</ContextDashboard.Provider>
		</Authenticated>
	);
};
